import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn, formatCurrency } from '@/lib/utils';
import { Check } from 'lucide-react';
import type { FlowConfig, RegistrationWizardState } from '@/types';

interface EventEntryStepProps {
  flowConfig: FlowConfig | null | undefined;
  wizardState: RegistrationWizardState;
  onComplete: (data: Partial<RegistrationWizardState>) => void;
}

type EntryType = 'free' | 'spectator' | 'paid';

export function EventEntryStep({ flowConfig, wizardState, onComplete }: EventEntryStepProps) {
  const [selectedType, setSelectedType] = useState<EntryType | null>(
    wizardState.eventRegistration.entryType || null
  );

  const entryTypes = flowConfig?.entryTypes || {
    free: { enabled: true, name: 'Free Entry', benefits: [] },
    spectator: { enabled: false, name: 'Spectator Pass', price: 1000, benefits: [] },
    paid: { enabled: true, name: 'Supporter Entry', price: 2500, benefits: [] },
  };

  const handleContinue = () => {
    if (!selectedType) return;

    const entryFee = selectedType === 'free' ? 0 :
      (selectedType === 'spectator' ? entryTypes.spectator.price :
        entryTypes.paid.price) || 0;

    onComplete({
      eventRegistration: {
        wantsToPay: selectedType !== 'free',
        entryType: selectedType,
        entryFee,
      },
    });
  };

  const renderEntryOption = (type: EntryType, config: typeof entryTypes.free | typeof entryTypes.spectator | typeof entryTypes.paid) => {
    if (!config.enabled) return null;

    const isSelected = selectedType === type;
    const price = type === 'free' ? 0 : ('price' in config ? config.price ?? 0 : 0);

    return (
      <Card
        key={type}
        className={cn(
          'cursor-pointer transition-all',
          isSelected ? 'ring-2 ring-primary border-primary' : 'hover:border-primary/50'
        )}
        onClick={() => setSelectedType(type)}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl">{config.name}</CardTitle>
            <div className={cn(
              'w-6 h-6 rounded-full border-2 flex items-center justify-center',
              isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
            )}>
              {isSelected && <Check className="w-4 h-4 text-primary-foreground" />}
            </div>
          </div>
          <CardDescription className="text-lg font-semibold">
            {price === 0 ? 'Free' : formatCurrency(price)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {config.description && (
            <p className="text-muted-foreground mb-4">{config.description}</p>
          )}
          {config.benefits && config.benefits.length > 0 && (
            <ul className="space-y-2">
              {config.benefits.map((item, index) => (
                <li key={index} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>{item.benefit}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-heading font-bold">Choose Your Entry Type</h2>
        <p className="text-muted-foreground mt-2">
          Select how you'd like to participate in the event
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {renderEntryOption('free', entryTypes.free)}
        {renderEntryOption('spectator', entryTypes.spectator)}
        {renderEntryOption('paid', entryTypes.paid)}
      </div>

      <div className="flex justify-center">
        <Button
          size="lg"
          onClick={handleContinue}
          disabled={!selectedType}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
